import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'

/**
 * Simplified onboarding for end users getting their company certified.
 * Focus: Products → Documents → Checklists → Evidence linking
 *
 * Consultant/multi-org features will be added later.
 */

export interface OnboardingStep {
  id: string
  title: string
  description: string
  path: string
  completed: boolean
  icon: string
  highlight?: string
}

// Core compliance journey - keep it simple
const coreSteps: Omit<OnboardingStep, 'completed'>[] = [
  {
    id: 'organization',
    title: 'Set Up Your Organization',
    description: 'Add your company name, sector, and contact details',
    path: '/company',
    icon: 'Building2',
    highlight: 'Your company identity for auditors',
  },
  {
    id: 'products',
    title: 'Add Your Products',
    description: 'Start by adding the products that need compliance certification',
    path: '/products',
    icon: 'Package',
    highlight: 'Everything else links to your products',
  },
  {
    id: 'documents',
    title: 'Upload Documents',
    description: 'Upload test reports, certificates, and technical documentation',
    path: '/documents',
    icon: 'FileText',
    highlight: 'AI helps categorise them automatically',
  },
  {
    id: 'checklists',
    title: 'Start a Checklist',
    description: 'Use pre-built templates for CE Marking, UKCA, or ISO standards',
    path: '/checklists',
    icon: 'ClipboardCheck',
    highlight: 'Track every requirement systematically',
  },
  {
    id: 'evidence',
    title: 'Link Evidence',
    description: 'Connect your documents to checklist items as proof of compliance',
    path: '/checklists',
    icon: 'Link',
    highlight: 'The magic happens here - audit-ready in seconds',
  },
  {
    id: 'team',
    title: 'Invite Your Team',
    description: 'Add team members so they can contribute to compliance work',
    path: '/team',
    icon: 'Users',
    highlight: 'Collaboration makes compliance faster',
  },
]

// Feature spotlights - highlight key value features once
export interface FeatureTour {
  id: string
  title: string
  description: string
  targetSelector?: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  shown: boolean
}

const defaultFeatureTours: Omit<FeatureTour, 'shown'>[] = [
  {
    id: 'ai-chatbot',
    title: 'Your AI Assistant',
    description: 'Ask questions like "What documents are expiring?" or "Show me incomplete checklists". Try it!',
    targetSelector: '[aria-label="Open AI Assistant"]',
    position: 'top',
  },
  {
    id: 'evidence-linking',
    title: 'Link Evidence Here',
    description: 'This is the key feature! Click the link icon to attach documents as proof. When auditors ask, your evidence is ready.',
    position: 'left',
  },
  {
    id: 'qms-health',
    title: 'Click for Details',
    description: 'These percentages are clickable. Tap any one to see exactly what\'s missing or needs attention.',
    position: 'bottom',
  },
]

// Persisted state
interface OnboardingState {
  completedSteps: string[]
  shownTours: string[]
  wizardDismissed: boolean
  checklistDismissed: boolean
  quickStartUsed: boolean
}

const defaultState: OnboardingState = {
  completedSteps: [],
  shownTours: [],
  wizardDismissed: false,
  checklistDismissed: false,
  quickStartUsed: false,
}

interface OnboardingContextType {
  steps: OnboardingStep[]
  completedSteps: string[]
  featureTours: FeatureTour[]
  isWizardDismissed: boolean
  isChecklistDismissed: boolean
  isQuickStartUsed: boolean
  isLoading: boolean
  completionPercentage: number
  currentStep: OnboardingStep | null
  isOnboardingComplete: boolean

  completeStep: (stepId: string) => Promise<void>
  markTourShown: (tourId: string) => Promise<void>
  dismissWizard: () => Promise<void>
  dismissChecklist: () => Promise<void>
  markQuickStartUsed: () => Promise<void>
  resetOnboarding: () => Promise<void>
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined)

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth()
  const [state, setState] = useState<OnboardingState>(defaultState)
  const [isLoading, setIsLoading] = useState(true)

  // Load state from database (preferred) or localStorage (fallback)
  useEffect(() => {
    async function loadState() {
      // If user has org, use database as source of truth
      if (profile?.organization_id) {
        try {
          const { data: org } = await supabase
            .from('organizations')
            .select('settings')
            .eq('id', profile.organization_id)
            .single()

          if (org?.settings && typeof org.settings === 'object' && 'onboarding' in org.settings) {
            const dbState = org.settings.onboarding as OnboardingState
            setState(dbState)
            localStorage.setItem('logidocs_onboarding', JSON.stringify(dbState))
            setIsLoading(false)
            return
          }
        } catch (error) {
          const supaErr = error as { code?: string; message?: string }
          console.error('Failed to load onboarding state:', supaErr.code, supaErr.message)
        }
      }

      // Fallback to localStorage (pre-org or DB miss)
      const localState = localStorage.getItem('logidocs_onboarding')
      if (localState) {
        try {
          setState(JSON.parse(localState))
        } catch {
          // Invalid JSON, use default
        }
      }

      setIsLoading(false)
    }

    loadState()
  }, [profile?.organization_id])

  // Save state — accepts an updater function to avoid lost updates from stale closures
  const saveState = useCallback(async (updater: (prev: OnboardingState) => OnboardingState) => {
    let newState!: OnboardingState
    let changed = true
    setState(prev => {
      newState = updater(prev)
      changed = newState !== prev
      return newState
    })
    if (!changed) return
    localStorage.setItem('logidocs_onboarding', JSON.stringify(newState))

    if (!profile?.organization_id) return

    try {
      const { data: org, error: fetchErr } = await supabase
        .from('organizations')
        .select('settings')
        .eq('id', profile.organization_id)
        .single()

      if (fetchErr) {
        console.error('Failed to fetch org settings for onboarding save:', fetchErr.code, fetchErr.message)
        return
      }

      const currentSettings = (org?.settings as Record<string, unknown>) || {}

      await supabase
        .from('organizations')
        .update({
          settings: { ...currentSettings, onboarding: newState },
        })
        .eq('id', profile.organization_id)
    } catch (error) {
      console.error('Failed to save onboarding state:', error)
    }
  }, [profile?.organization_id])

  // Build steps with completion status — memoized so consumers don't see new
  // references on every render of the provider.
  const steps: OnboardingStep[] = useMemo(
    () => coreSteps.map(s => ({
      ...s,
      completed: state.completedSteps.includes(s.id),
    })),
    [state.completedSteps]
  )

  // Build feature tours with shown status — memoized for the same reason.
  const featureTours: FeatureTour[] = useMemo(
    () => defaultFeatureTours.map(t => ({
      ...t,
      shown: state.shownTours.includes(t.id),
    })),
    [state.shownTours]
  )

  // Computed values — memoized so downstream effects don't fire on every render.
  const completedCount = useMemo(
    () => steps.filter(s => s.completed).length,
    [steps]
  )
  const completionPercentage = useMemo(
    () => Math.round((completedCount / steps.length) * 100),
    [completedCount, steps.length]
  )
  const currentStep = useMemo(
    () => steps.find(s => !s.completed) || null,
    [steps]
  )
  const isOnboardingComplete = completedCount === steps.length

  // Actions — wrapped in useCallback so they're stable across renders. Without
  // this, every render produced new function references which propagated to
  // every consumer's effect deps and caused a render storm.
  const completeStep = useCallback(async (stepId: string) => {
    await saveState(prev => {
      if (prev.completedSteps.includes(stepId)) return prev
      return { ...prev, completedSteps: [...prev.completedSteps, stepId] }
    })
  }, [saveState])

  const markTourShown = useCallback(async (tourId: string) => {
    await saveState(prev => {
      if (prev.shownTours.includes(tourId)) return prev
      return { ...prev, shownTours: [...prev.shownTours, tourId] }
    })
  }, [saveState])

  const dismissWizard = useCallback(async () => {
    await saveState(prev => ({ ...prev, wizardDismissed: true }))
  }, [saveState])

  const dismissChecklist = useCallback(async () => {
    await saveState(prev => ({ ...prev, checklistDismissed: true }))
  }, [saveState])

  const markQuickStartUsed = useCallback(async () => {
    await saveState(prev => ({ ...prev, quickStartUsed: true }))
  }, [saveState])

  const resetOnboarding = useCallback(async () => {
    await saveState(() => defaultState)
  }, [saveState])

  // Memoize the context value so consumers only re-render when something
  // actually changes — not on every render of the provider.
  const value = useMemo<OnboardingContextType>(() => ({
    steps,
    completedSteps: state.completedSteps,
    featureTours,
    isWizardDismissed: state.wizardDismissed,
    isChecklistDismissed: state.checklistDismissed,
    isQuickStartUsed: state.quickStartUsed,
    isLoading,
    completionPercentage,
    currentStep,
    isOnboardingComplete,
    completeStep,
    markTourShown,
    dismissWizard,
    dismissChecklist,
    markQuickStartUsed,
    resetOnboarding,
  }), [
    steps,
    state.completedSteps,
    state.wizardDismissed,
    state.checklistDismissed,
    state.quickStartUsed,
    featureTours,
    isLoading,
    completionPercentage,
    currentStep,
    isOnboardingComplete,
    completeStep,
    markTourShown,
    dismissWizard,
    dismissChecklist,
    markQuickStartUsed,
    resetOnboarding,
  ])

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  )
}
export function useOnboardingContext() {
  const context = useContext(OnboardingContext)
  if (context === undefined) {
    throw new Error('useOnboardingContext must be used within an OnboardingProvider')
  }
  return context
}
